import React, { useState } from 'react';
import { Button } from '@/components/Button';
import { Input } from '@/components/Input';
import { addNotification, notificationStates } from '@components/Notification';
import firebase from '@config/firebase.config';
import { useSelector } from 'react-redux';
import styles from './Settings.module.scss';
import { textVariantsEnum, Text, fontWeightEnum } from '@/components/Text';
const database = firebase.firestore();

export const PolicyConfigurationModal = ({
  isAdmin,
  onClose,
  getCurrentLeavePolicy,
}) => {
  const [paidLeaveAllocation, setPaidLeaveAllocation] = useState('');
  const [leavePolicyLoading, setLeavePolicyLoading] = useState(false);
  const userProfile = useSelector((state) => state.auth.profile);

  const toggleLeavePolicy = () => {
    // Security check: Only admins can access leave policy configuration
    if (!isAdmin) {
      addNotification({
        message:
          'Access denied: Only administrators can configure leave policy',
        level: notificationStates.error,
      });
      return;
    }
    onClose();
  };
  const handleLeavePolicySubmit = async (e) => {
    e.preventDefault();

    // Security check: Only admins can configure leave policy
    if (!isAdmin) {
      addNotification({
        message:
          'Access denied: Only administrators can configure leave policy',
        level: notificationStates.error,
      });
      return;
    }

    if (!paidLeaveAllocation || paidLeaveAllocation < 0) {
      addNotification({
        message: 'Please enter a valid number of paid leave days',
        level: notificationStates.error,
      });
      return;
    }

    setLeavePolicyLoading(true);

    try {
      const currentYear = new Date().getFullYear();
      const docRef = database
        .collection('companyDatabase')
        .doc(userProfile.activeCompany.companyId)
        .collection('leavePolicy')
        .doc(currentYear.toString());

      // Check if document exists to determine if we're creating or updating
      const doc = await docRef.get();
      const isNewDocument = !doc.exists;

      const policyData = {
        year: currentYear,
        paidLeaveAllocation: parseInt(paidLeaveAllocation),
        updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
        updatedBy: userProfile.id,
        companyId: userProfile.activeCompany.companyId,
      };

      // Only set createdAt and createdBy for new documents
      if (isNewDocument) {
        policyData.createdAt = firebase.firestore.FieldValue.serverTimestamp();
        policyData.createdBy = userProfile.id;
      }

      await docRef.set(policyData, { merge: true });

      // Refetch the data to get the actual timestamp
      await getCurrentLeavePolicy();

      toggleLeavePolicy();

      addNotification({
        message: 'Leave policy updated successfully',
        level: notificationStates.success,
      });
    } catch (error) {
      console.error('Error updating leave policy:', error);
      addNotification({
        message: 'Failed to update leave policy',
        level: notificationStates.error,
      });
    } finally {
      setLeavePolicyLoading(false);
    }
  };

  return (
    <div className={styles.policyConfigurationModal}>
      <Text
        variant={textVariantsEnum.Text_sm}
        fontWeight={fontWeightEnum.Medium}
      >
        Annual Leave Policy Configuration
      </Text>
      <Input
        type="number"
        label="Paid Leave Days per Year"
        value={paidLeaveAllocation}
        onChange={(e) => setPaidLeaveAllocation(e.target.value)}
        placeholder="Enter number of paid leave days (e.g., 25)"
        min="0"
        max="365"
        required
      />
      <Text
        variant={textVariantsEnum.Text_xs}
        fontWeight={fontWeightEnum.Normal}
      >
        This will be the annual allocation for all employees in your company
      </Text>
      <Button
        onClick={handleLeavePolicySubmit}
        disabled={leavePolicyLoading}
        text={leavePolicyLoading ? 'Saving...' : 'Save Policy'}
      />
    </div>
  );
};
